/**
 * sirus/HelpPanel — R232q21 Phase 4.
 *
 * Sirus 3.3.5a-style help / FAQ window. Toggled via the `?` micro-button
 * (panel flag `help`). Eight expandable category rows; clicking one
 * toggles its body open and renders a paragraph of Russian help text.
 *
 * Categories:
 *   – Управление, Боевая система, Реалмы, Кастомизация, Подземелья,
 *     PvP, Магазин, Контакты.
 *
 * The text is intentionally short — we'll grow it after the systems land.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Categories
 * ──────────────────────────────────────────────────────────────── */

interface Topic {
  id: string;
  label: string;
  body: string;
}

const TOPICS: Topic[] = [
  {
    id: 'controls',
    label: 'Управление',
    body:
      'Передвижение — WASD, прыжок — Пробел. Камера вращается правой кнопкой мыши. ' +
      'Цель выделяется левой кнопкой по NPC или игроку, либо клавишей Tab. ' +
      'Чат открывается на Enter, выход к меню — на Esc. Стандартные горячие клавиши: ' +
      'C — лист персонажа, P — книга умений, N — стезя талантов, L — журнал, B — сумки.',
  },
  {
    id: 'combat',
    label: 'Боевая система',
    body:
      'Бой строится на глобальном кулдауне в 1.5 секунды и ограниченном пуле силы (мана, ярость, энергия — у каждого класса своё). ' +
      'Автоатака запускается двойным щелчком по цели или клавишей умения «Атаковать». ' +
      'Положительные эффекты — баффы — отображаются в правом верхнем углу, дебаффы — на цели. ' +
      'Воскрешение возможно у духа в кладбище зоны.',
  },
  {
    id: 'realms',
    label: 'Реалмы',
    body:
      'Игровой мир разделён на отдельные реалмы (серверы). Каждый реалм имеет независимое экономическое пространство, гильдии и аукцион. ' +
      'Перенос персонажа между реалмами оформляется в магазине услуг. ' +
      'Текущий реалм можно увидеть на экране выбора персонажей и в строке заголовка чата.',
  },
  {
    id: 'customization',
    label: 'Кастомизация',
    body:
      'При создании персонажа выбираются раса, класс, пол и косметические признаки (причёска, цвет кожи, лицо). ' +
      'Внешний вид одетых предметов соответствует реальной экипировке — модульная замена брони появится в одном из ближайших обновлений. ' +
      'Услуги «Смена расы», «Смена пола» и «Переименование» оформляются через магазин услуг.',
  },
  {
    id: 'dungeons',
    label: 'Подземелья',
    body:
      'Подземелья делятся на 5-человечные (норм. и героик) и 10/25-человечные рейды. ' +
      'В героик-режиме увеличены здоровье и урон врагов, но и награды качественнее. ' +
      'Сбор группы возможен через окно «Поиск группы» (клавиша I) либо вручную в чате.',
  },
  {
    id: 'pvp',
    label: 'PvP',
    body:
      'PvP-награды копятся в виде очков чести и очков арены. ' +
      'Поля боя открываются с 60-го уровня, рейтинговая арена — с 80-го. ' +
      'Обмен наград производится у торговца чести в столице фракции. ' +
      'Вход в очередь — через окно «Поиск группы», вкладки «Поля боя» и «Арена».',
  },
  {
    id: 'shop',
    label: 'Магазин',
    body:
      'Магазин предлагает кристаллы Шторма (внутриигровая премиум-валюта), косметические предметы, услуги и Battle Pass. ' +
      'Покупка совершается через платёжный шлюз LavaTop — данные карты не передаются на наш сервер. ' +
      'Никакие предметы магазина не дают преимущества в бою (no pay-to-win).',
  },
  {
    id: 'contacts',
    label: 'Контакты',
    body:
      'Поддержка: team@aiwari.com. Баг-репорты: через кнопку «Сообщить о проблеме» в главном меню. ' +
      'Сообщество: Telegram @aiwari_official. Открытые задачи и идеи: aiwari.com/roadmap.',
  },
];

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function HelpPanel() {
  const close = () => useUiPanels.getState().set('help', false);
  const [open, setOpen] = useState<string | null>('controls');

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()}>
        <SirusFrame size="md" style={{ width: 540, height: 540, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Помощь</span>
            <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
          </div>

          <div style={bodyStyle}>
            <div style={leadStyle}>
              Краткие справки по основным системам игры. Раскройте раздел, чтобы увидеть подробности.
            </div>

            <div style={listStyle}>
              {TOPICS.map((t) => {
                const expanded = open === t.id;
                return (
                  <div key={t.id} style={{
                    border: '1px solid #2A2014',
                    background: expanded ? 'rgba(201,169,106,0.08)' : 'rgba(11,9,7,0.4)',
                  }}>
                    <button
                      type="button"
                      onClick={() => setOpen(expanded ? null : t.id)}
                      style={{
                        ...rowBtnStyle,
                        color: expanded ? '#FFD479' : 'var(--aiwari-fg, #E8E1DA)',
                      }}
                    >
                      <span>{t.label}</span>
                      <span aria-hidden style={{ fontSize: 12 }}>{expanded ? '▾' : '▸'}</span>
                    </button>
                    {expanded && (
                      <div style={textStyle}>{t.body}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 8,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 18,
  letterSpacing: 0.8,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const closeBtnStyle: CSSProperties = {
  width: 28, height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer',
  fontFamily: 'inherit',
};

const bodyStyle: CSSProperties = {
  height: 460,
  marginTop: 10,
  overflowY: 'auto',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const leadStyle: CSSProperties = {
  marginBottom: 10,
  fontSize: 13,
  fontStyle: 'italic',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const rowBtnStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 14,
  letterSpacing: 0.4,
  cursor: 'pointer',
  textAlign: 'left',
};

const textStyle: CSSProperties = {
  padding: '0 14px 12px',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

export default HelpPanel;
